import type { ReactNode } from 'react';
import type { RequestStatus, RequestKind } from '@magimp/shared';
import { RequestsFilterChip } from './RequestsFilterChip.js';

type StatusFilterId = 'all' | RequestStatus;
type SlaFilterId = 'hot' | 'overdue';

interface StatusFilterDef {
  id: StatusFilterId;
  label: string;
}
interface SlaFilterDef {
  id: SlaFilterId;
  label: string;
}
interface KindFilterDef {
  id: RequestKind;
  label: string;
}

interface RequestsListFilterStripProps {
  chipsOpen: boolean;
  chipsGroupId: string;
  filters: readonly StatusFilterDef[];
  slaFilters: readonly SlaFilterDef[];
  kindFilters: readonly KindFilterDef[];
  counts: Record<string, number | undefined>;
  isStatusActive: (id: StatusFilterId) => boolean;
  isSlaActive: (id: SlaFilterId) => boolean;
  selectedKind: RequestKind | null;
  unreadOnly: boolean;
  unreadLabel: string;
  onToggleStatus: (id: StatusFilterId) => void;
  onToggleSla: (id: SlaFilterId) => void;
  onToggleUnread: () => void;
  onToggleKind: (id: RequestKind) => void;
}

function Divider(): ReactNode {
  return (
    <div
      style={{
        width: 1,
        height: 20,
        background: 'var(--border-1)',
        margin: '0 4px',
        flex: 'none',
      }}
    />
  );
}

/**
 * @aiPurpose Filter-chip row for `RequestsListPage`. Order is fixed by
 *   spec: status chips -> divider -> SLA chips -> divider -> Unread chip
 *   -> kind chips. (#583, #1054) Pure presentation — labels, counts,
 *   active predicates, and handlers flow in from the parent.
 */
export function RequestsListFilterStrip({
  chipsOpen,
  chipsGroupId,
  filters,
  slaFilters,
  kindFilters,
  counts,
  isStatusActive,
  isSlaActive,
  selectedKind,
  unreadOnly,
  unreadLabel,
  onToggleStatus,
  onToggleSla,
  onToggleUnread,
  onToggleKind,
}: RequestsListFilterStripProps) {
  return (
    <div
      data-testid="requests-status-chips"
      style={{
        padding: '10px 24px',
        // WHY (#1526): the outer bar is hidden (not unmounted) while chips
        //   are collapsed so it never shows as an empty padded strip; the
        //   inner `chipsGroupId` element stays in the DOM so the "Statuses"
        //   toggle's aria-controls always resolves.
        display: chipsOpen ? 'flex' : 'none',
        alignItems: 'center',
        gap: 8,
        flexWrap: 'wrap',
        borderBottom: '1px solid var(--border-1)',
        background: 'var(--bg-1)',
        flex: 'none',
      }}
    >
      {/* WHY (#1507): the chip group collapses by default behind the
          header "Statuses" toggle. The wrapper stays mounted in both
          states so `aria-controls` always resolves; `display:'contents'`
          when open keeps the expanded layout byte-for-byte identical to
          the flat flex row (the wrapper is transparent to flexbox). */}
      <div id={chipsGroupId} style={{ display: chipsOpen ? 'contents' : 'none' }}>
        {filters.map((f) => (
          <RequestsFilterChip
            key={f.id}
            chipId={f.id}
            label={f.label}
            count={counts[f.id]}
            active={isStatusActive(f.id)}
            onClick={() => onToggleStatus(f.id)}
          />
        ))}
        <Divider />
        {slaFilters.map((f) => (
          <RequestsFilterChip
            key={f.id}
            chipId={f.id}
            label={f.label}
            count={counts[f.id]}
            active={isSlaActive(f.id)}
            onClick={() => onToggleSla(f.id)}
          />
        ))}
        {/* WHY (#583): a second separator visually anchors the Unread
            chip as a distinct group, sibling to SLA but conceptually
            unrelated. The `flexWrap: 'wrap'` parent keeps the separator
            and chip on the next line together on narrow screens. */}
        <Divider />
        <RequestsFilterChip
          chipId="unread"
          label={unreadLabel}
          count={counts['unread']}
          active={unreadOnly}
          onClick={onToggleUnread}
        />
        {/* WHY (#1054): kind chips follow Unread, last in the chip row,
            per spec: status -> SLA -> Unread -> kind. */}
        {kindFilters.map((f) => (
          <RequestsFilterChip
            key={f.id}
            chipId={f.id}
            label={f.label}
            count={counts[`kind:${f.id}`]}
            active={selectedKind === f.id}
            onClick={() => onToggleKind(f.id)}
          />
        ))}
      </div>
    </div>
  );
}
